import { IconPlus } from '@tabler/icons-react';
import { ColDef, IRowNode } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import DataTable from 'components/tables/DataTable';
import { hrActionCellVersion, HrConfigurationActions } from 'components/tables/data/hr/HrActions';
import {
  HrConfigurationNameCell,
  HrIdCell,
  HrLongTextCell,
  HrRecurrenceCell,
  HrScopeCell,
  HrTermKindCell,
  HrTextCell,
} from 'components/tables/data/hr/HrTableCells';
import { HrSelectFloatingFilter } from 'components/tables/filters/hr/HrFloatingFilters';
import panic from 'errors/panic';
import {
  getResponsiveActionsColumnWidth,
  SINGLE_ACTIONS_COLUMN_WIDTH,
  useResponsiveDesign,
} from 'hooks/use-responsive-design';
import { ROLE_ADMIN_ID } from 'model/Role';
import { formatHrRecurrence, formatHrTermKind } from 'pages/hr-module/hr-formatters';
import { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH, HR_CREATE_TERM_TYPE_PAGE_PATH, HR_EDIT_TERM_TYPE_PAGE_PATH } from 'routes/paths';

/** Administrator list of global and multi-organization HR term types. */
export default function HrTermTypesTable() {
  const { getAction, roleId } = useApi();
  const { tableActionMode } = useResponsiveDesign();
  const [refreshToken, setRefreshToken] = useState(0);

  const load = useCallback(async () => {
    const response = await getAction('HrListTermTypes')({ query: { filters: { includeArchived: true } } });

    return response.map((termType) => ({
      ...termType,
      rawStatus: termType.status,
      _lifecycleActive: termType.status === 'active' ? '1' : '0',
    }));
  }, [getAction]);

  const toggleStatus = useCallback(
    async (termType: any, node: IRowNode<any>) => {
      try {
        const nextStatus = termType.rawStatus === 'active' ? 'archived' : 'active';
        const updatedTermType = await getAction('HrSetTermTypeStatus')({
          parameters: { termTypeId: String(termType.termTypeId) },
          payload: {
            version: termType.version,
            status: nextStatus,
          },
        });
        const status = updatedTermType.status ?? nextStatus;
        node.setData({
          ...termType,
          ...updatedTermType,
          status,
          rawStatus: status,
          _lifecycleActive: status === 'active' ? '1' : '0',
        });
        setRefreshToken((value) => value + 1);
      } catch (caught) {
        panic(caught as Error);
      }
    },
    [getAction]
  );

  const columns = useMemo<ColDef[]>(() => {
    const actionsWidth = getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode);

    return [
      {
        field: 'termTypeId',
        headerName: 'ID',
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        sortable: true,
        unSortIcon: true,
        autoHeaderHeight: true,
        cellRenderer: HrIdCell,
      },
      {
        field: 'name',
        headerName: 'Názov',
        minWidth: 240,
        sortable: true,
        unSortIcon: true,
        autoHeaderHeight: true,
        cellRenderer: HrConfigurationNameCell,
      },
      {
        field: 'description',
        headerName: 'Popis',
        minWidth: 300,
        autoHeight: true,
        autoHeaderHeight: true,
        cellRenderer: HrLongTextCell,
      },
      {
        field: 'variants',
        headerName: 'Varianty',
        minWidth: 240,
        autoHeaderHeight: true,
        valueGetter: ({ data }) => (data?.variants ?? []).map(({ name }: any) => name).join(', '),
        cellRenderer: HrTextCell,
      },
      {
        field: 'organizations',
        headerName: 'Vymedzenie štruktúry',
        minWidth: 300,
        valueGetter: ({ data }) =>
          data?.isGlobal
            ? 'Všetky organizácie'
            : (data?.organizations ?? []).map(({ organizationName }: any) => organizationName).join(', '),
        getQuickFilterText: ({ data }) =>
          data?.isGlobal
            ? 'Všetky organizácie'
            : (data?.organizations ?? []).map(({ organizationName }: any) => organizationName).join(' '),
        autoHeaderHeight: true,
        cellRenderer: HrScopeCell,
      },
      {
        field: 'kind',
        headerName: 'Druh termínu',
        minWidth: 210,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: HrSelectFloatingFilter.configure({
          filterKey: 'kind',
          placeholder: 'Druh termínu',
          data: [
            { value: 'ordinary', label: 'Bežný termín' },
            { value: 'technician-certificate', label: 'Osvedčenie technika' },
          ],
        }),
        autoHeaderHeight: true,
        valueFormatter: ({ value }) => formatHrTermKind(value),
        cellRenderer: HrTermKindCell,
      },
      {
        field: 'defaultRecurrence',
        headerName: 'Predvolené opakovanie',
        minWidth: 230,
        autoHeaderHeight: true,
        valueFormatter: ({ value }) => formatHrRecurrence(value),
        cellRenderer: HrRecurrenceCell,
      },
      {
        field: '_lifecycleActive',
        headerName: 'Stav',
        hide: true,
        filter: true,
      },
      {
        field: '_actions',
        headerName: 'Akcie',
        valueGetter: hrActionCellVersion,
        pinned: 'right',
        width: actionsWidth,
        minWidth: actionsWidth,
        maxWidth: actionsWidth,
        autoHeaderHeight: true,
        cellRenderer: HrConfigurationActions,
        cellRendererParams: {
          editTarget: (termType: any) =>
            HR_EDIT_TERM_TYPE_PAGE_PATH.insert({ termTypeId: String(termType.termTypeId) }),
          entityNameAccusative: 'typ HR termínu',
          onToggle: toggleStatus,
        },
      },
    ];
  }, [tableActionMode, toggleStatus]);

  if (roleId !== ROLE_ADMIN_ID) {
    return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  }

  return (
    <DataTable
      tableId="hr-term-types"
      title="Typy HR termínov"
      action={load}
      columns={columns}
      dataRefreshToken={`term-types:${refreshToken}`}
      addButtonText="Pridať typ termínu"
      addButtonIcon={<IconPlus stroke={1.5} />}
      addButtonTarget={HR_CREATE_TERM_TYPE_PAGE_PATH.original}
      toggleDiscardedLabel="Zobraziť vyradené"
      discardedFilterField="_lifecycleActive"
      dataExport={{
        modalTitle: 'Exportovať typy HR termínov',
        fileName: 'hr-typy-terminov.xlsx',
        columnKeys: ['termTypeId', 'name', 'description', 'variants', 'organizations', 'kind', 'defaultRecurrence'],
      }}
      getRowId={({ data }) => String((data as { termTypeId: number }).termTypeId)}
    />
  );
}
